/// <amd-module name="TFS/Admin/Views/Teams/Components/TeamsPageContentView" />
import * as React from "react";
import { connect } from "react-redux";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import * as Constants from "TFS/Admin/Groups/Common/Constants";
import { IGroupsViewState, PermissionsGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import { MemberOfPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/MemberOfPivotActions";
import { GroupMembersPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupMembersPivotActions";
import * as ViewSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { convertGraphMemberToIIdentity } from "TFS/Admin/Groups/Common/Util/CommonTypeConverters";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ContributedTabProvider } from "VSS/Features/Frame/ContributedTabProvider";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { FilterBar } from "VSSUI/FilterBar";
import { CustomHeader, HeaderDescription, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { Page } from "VSSUI/Page";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent } from "VSSUI/Tabs";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { IHubViewState } from "VSSUI/Utilities/HubViewState";
import { IVssPersonaProps, VssPersona } from "VSSUI/VssPersona";

interface ITeamsContainerProps {
    hubViewState: IHubViewState;
    context: IVssComponentContext;
}

interface ITeamsPageContentViewOwnProps extends ITeamsContainerProps {
    selectedTeam: PermissionsGroup | undefined;
    selectedNavType: Constants.GroupsNavType;
    filterPlaceholder: string | undefined;
    updatedAvatarUrl?: string;
}

export type ITeamsPageContentViewProps = ITeamsPageContentViewOwnProps & typeof ActionsToDispatch;

interface TeamsPageContentViewState {}

export class TeamsPageContentView extends VssComponent<ITeamsPageContentViewProps, TeamsPageContentViewState> {
    private _tabProviders: IObservableArray<IVssContributedTab>;
    private _pageStateHistoryService: IPageStateHistoryService;
    private _selectedSubjectDescriptor: ObservableValue<string>;
    private _selectedTab: ObservableValue<string>;
    private _projectName: string;

    public constructor(props: ITeamsPageContentViewProps) {
        super(props);

        this._selectedTab = new ObservableValue(this._getDefaultSelectedTab());
        this._pageStateHistoryService = this.props.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        this._selectedSubjectDescriptor = new ObservableValue("");
        this._selectedSubjectDescriptor.subscribe(this.onSelectedSubjectDescriptorChanged);
        this._pageStateHistoryService.registerObservable(this._selectedSubjectDescriptor, "subjectDescriptor", HistoryBehavior.newEntry);
        this.props.initView();
        this.props.setIsTeamsPageFlag(true);
    }

    public render(): JSX.Element {
        this._tabProviders = new ContributedTabProvider({
            pageContext: this.props.context.pageContext,
            tabGroupIds: this._getSelectedTabGroup(),
            updateUrlOnPivotChange: true,
            props: {
                filter: this.props.hubViewState.filter
            }
        });

        const selectedGraphSubject = this.props.selectedTeam ? this.props.selectedTeam : "";
        const teamId = this.props.selectedTeam ? this.props.selectedTeam.originId : "";
        const routeValues = Constants.RouteValues;

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow flex-column" tabProviders={this._tabProviders} selectedTabId={this._selectedTab}>
                    <CustomHeader className="bolt-header-with-commandbar">
                        {selectedGraphSubject && !this.props.updatedAvatarUrl ? (
                            <Persona
                                className="display-members-persona"
                                size={PersonaSize.size72}
                                identity={convertGraphMemberToIIdentity(
                                    this.props.context.pageContext,
                                    selectedGraphSubject,
                                    this.props.selectedTeam ? this.props.selectedTeam.updateImage : false
                                )}
                            />
                        ) : null}
                        {this.props.updatedAvatarUrl ? (
                            <VssPersona {...this._getPersona()} size="extra-large" className="display-members-persona" />
                        ) : null}
                        <HeaderTitleArea>
                            <HeaderTitleRow>
                                <HeaderTitle className="text-ellipsis" titleSize={TitleSize.Large}>
                                    {this._getSelectedSubjectName()}
                                </HeaderTitle>
                            </HeaderTitleRow>
                            <HeaderDescription>
                                {this._getGroupDescription()}
                                {teamId && (
                                    <div className="flex-row team-links-margin">
                                        <span className="team-links-spacing">{Resources.RelevantLinks}</span>
                                        <div className="flex-wrap flex-row">
                                            <Link
                                                href={this._getTeamUrlForRoute(teamId, routeValues.Notifications)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={Resources.Notifications}
                                                className="team-links-spacing"
                                            >
                                                {Resources.Notifications}
                                            </Link>
                                            <span className="team-links-spacing">|</span>
                                            <Link
                                                href={this._getTeamUrlForRoute(teamId, routeValues.Dashboards)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={Resources.Dashboards}
                                                className="team-links-spacing"
                                            >
                                                {Resources.Dashboards}
                                            </Link>
                                            <span className="team-links-spacing">|</span>
                                            <Link
                                                href={this._getTeamUrlForRoute(teamId, routeValues.IterationAreaPaths)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={Resources.IterationAreaPaths}
                                                className="team-links-spacing"
                                            >
                                                {Resources.IterationAreaPaths}
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </HeaderDescription>
                        </HeaderTitleArea>
                    </CustomHeader>

                    <TabBar
                        onSelectedTabChanged={this._onSelectedTabChanged}
                        renderAdditionalContent={this._renderAdditionalContent}
                        className={"teams-hub-tab-container"}
                    />
                    <div className="page-content page-content-top">
                        <TabContent />
                    </div>
                </Page>
            </Surface>
        );
    }

    public componentDidMount(): void {
        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.Teams);

        this.props.logTelemetry(GroupsComponent.TeamsView, "Page loaded", {}, "", Feature.Teams);
    }

    public componentDidUpdate(prevProps: ITeamsPageContentViewProps) {
        const { selectedTeam } = this.props;
        const prevTeamId = prevProps.selectedTeam ? prevProps.selectedTeam.descriptor : "";

        const selectedTeamDescriptor = selectedTeam ? selectedTeam.descriptor : "";

        if (prevTeamId != selectedTeamDescriptor || prevProps.selectedNavType != this.props.selectedNavType) {
            this._onSelectedTabChanged(this._getDefaultSelectedTab()); // Get default tab for groups-details and user-details
        }

        if (selectedTeamDescriptor && this._selectedSubjectDescriptor.value != selectedTeamDescriptor) {
            this._selectedSubjectDescriptor.value = selectedTeamDescriptor;
        }
    }

    private _getPersona(): IVssPersonaProps {
        const avatarPersona = {
            suppressPersonaCard: true,
            displayName: this._getSelectedSubjectName(),
            showInitialsOnImageError: true,
            imageUrl: "",
            imgAltText: ""
        };

        if (this.props.updatedAvatarUrl) {
            (avatarPersona.imageUrl = this.props.updatedAvatarUrl), (avatarPersona.imgAltText = Resources.ImageAlt);
        }

        return avatarPersona;
    }

    // This will get called on view switch from UI and when browser navigation is used
    private onSelectedSubjectDescriptorChanged = (value: string, action: string): void => {
        const { selectedTeam } = this.props;
        const teamDescriptor = selectedTeam ? selectedTeam.descriptor : "";
        if (teamDescriptor && teamDescriptor != value) {
            this.props.initGroupsPivot();
        }
    };

    private _getGroupDescription(): string {
        const { selectedTeam } = this.props;
        return selectedTeam ? selectedTeam.description : "";
    }

    private _getSelectedSubjectName(): string {
        const { selectedTeam } = this.props;
        return selectedTeam ? selectedTeam.displayName : Resources.TeamsPageTitle;
    }

    private _onSelectedTabChanged = (newTabId: string) => {
        // TODO - hookup telemetry
        this._selectedTab.value = newTabId;
    };

    private _getDefaultSelectedTab(): string {
        switch (this.props.selectedNavType) {
            case Constants.GroupsNavType.TeamDetails:
                return Constants.groupMembersTab;

            default:
                return Constants.teamsTab;
        }
    }

    private _getSelectedTabGroup(): string[] {
        switch (this.props.selectedNavType) {
            case Constants.GroupsNavType.TeamDetails:
                return [Constants.TeamsPageDetailsTabGroup];

            default:
                return [Constants.TeamsPageTabGroup];
        }
    }

    private _renderAdditionalContent = (): JSX.Element | undefined => {
        if ([Constants.teamsTab, Constants.groupMembersTab].indexOf(this._selectedTab.value) !== -1) {
            return (
                <FilterBar filter={this.props.hubViewState.filter}>
                    <KeywordFilterBarItem
                        placeholder={this.props.filterPlaceholder || Resources.FilterTeams}
                        filterItemKey={Constants.SearchKeyword}
                    />
                </FilterBar>
            );
        }

        return undefined;
    };

    private _getTeamUrlForRoute(teamId: string, routeValue: string) {
        const routeUrl = this._getProjectSettingsRouteUrl(routeValue);

        return routeUrl.concat("?teamId=" + teamId);
    }

    private _getProjectSettingsRouteUrl(routeValue: string) {
        if (!this._projectName) {
            this._retrieveProject();
        }

        const routeId = Constants.ProjectSettingsRoute;

        const routeValues: { [key: string]: string } = {};
        routeValues[Constants.RouteParameters.Project] = this._projectName;
        routeValues[Constants.RouteParameters.AdminPivot] = routeValue;

        return routeUrl(this.props.context.pageContext, routeId, routeValues);
    }

    private _retrieveProject() {
        const routeValues: { [key: string]: string } = {};
        const tfsPageService = this.props.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            this._projectName = tfsPageData.project.name;
        }
    }
}

// Update component props whenever the store's state changes
function mapStateToProps(state: IGroupsViewState, providedProps: ITeamsContainerProps): Partial<ITeamsPageContentViewProps> {
    return {
        ...providedProps,
        selectedTeam: ViewSelectors.getSelectedGroup(state),
        selectedNavType: ViewSelectors.getSelectedNavType(state),
        filterPlaceholder: ViewSelectors.setFilterPlaceholder(state),
        updatedAvatarUrl: ViewSelectors.getUpdatedAvatarUrl(state)
    };
}

// Hook up action creators to reducer
const ActionsToDispatch = {
    initView: GroupsViewActions.initView,
    logTelemetry: GroupsViewActions.logTelemetry, // TODO - update the telemetry to Feature.ProjectGroups
    closeAddGroupPanel: GroupsPivotActions.closeAddGroupPanel,
    openAddMemberPanel: GroupMembersPivotActions.openAddMemberPanel,
    openAddMemberOfPanel: MemberOfPivotActions.openAddMemberOfPanel,
    initGroupsPivot: GroupsPivotActions.initView,
    setIsTeamsPageFlag: GroupsViewActions.setIsTeamsPageFlag
};

export const ConnectedTeamsPageContentView = connect(mapStateToProps, ActionsToDispatch)(TeamsPageContentView);
